import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetStateTaxTableDocument } from '@willow/graphql-iso/src/app';
import { CompanyId, StateAbbreviation } from '@willow/types-iso';

import { Loader } from '../../Loader/Loader';
import { Options, WillowSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  stateAbbreviation: StateAbbreviation;
  fieldName: FieldPathByValue<T, string | undefined>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
};

export function ControlledAddressCountySelect<T extends FieldValues>({
  companyId,
  stateAbbreviation,
  control,
  fieldName,
  hideErrorMessage = false,
  label,
  placeholder,
  required,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { loading, data, error } = useQuery(GetStateTaxTableDocument, {
    variables: {
      companyId,
      stateAbbreviation,
    },
  });
  if (loading || !data) return <Loader />;
  if (error) return <>Something went wrong, please try again</>;

  const counties = (data && data.company.stateTaxTable.counties) ?? [];
  const OPTIONS: Options = counties.map((c) => {
    return { value: c.name, label: c.name };
  });

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}

      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowSelect
            placeholder={placeholder || 'Select county'}
            options={OPTIONS}
            value={value ? OPTIONS.filter((r) => value === r.value) : null}
            isClearable
            onChange={(e) => onChange(e?.value ?? '')}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
